'use client';

import { useEffect, useRef, useState } from 'react';

import { useTeamConfig } from '@/app/(football)/[team]/_context/context';
import { trackEvent } from '@/libs/analytics/events';
import { useGetProductListWithInitQuery } from '@/store/server/common/product/product.query';

import type { LayoutFilterValue } from './LayoutFilter';
import LayoutFilter from './LayoutFilter';
import ListItem from './ListItem';
import ListItemSkeleton from './ListItemSkeleton';

export default function ListSection() {
  const { originalsName, teamSlug } = useTeamConfig();
  const [layoutFilterValue, setLayoutFilterValue] = useState<LayoutFilterValue>('list');
  const hasTrackedRef = useRef(false);

  const { data: productList, isLoading: isProductListLoading } = useGetProductListWithInitQuery({
    originalsName,
    subscribeYn: 'N',
    employeeYn: 'N',
  });

  useEffect(() => {
    if (!isProductListLoading && productList && !hasTrackedRef.current) {
      hasTrackedRef.current = true;
      trackEvent({
        name: 'view_item_list',
        params: {
          list_name: 'store',
          items: productList.productList.map((product, index) => ({
            item_id: product.productId,
            item_name: product.name,
            item_brand: teamSlug,
            item_category: 'store',
            price: product.price,
            index,
          })),
        },
      });
    }
  }, [isProductListLoading, productList, teamSlug]);

  const resolvedLayout: LayoutFilterValue =
    !isProductListLoading && productList && productList.productList.length >= 2 ? 'grid' : layoutFilterValue;

  const handleLayoutChange = (value: LayoutFilterValue) => {
    setLayoutFilterValue(value);
  };

  return (
    <section className="flex w-full flex-col items-center">
      <LayoutFilter layoutFilterValue={resolvedLayout} handleLayoutChange={handleLayoutChange} />
      <ul
        className={resolvedLayout === 'list' ? 'flex w-full flex-col gap-8' : 'grid w-full grid-cols-2 gap-x-3 gap-y-8'}
      >
        {isProductListLoading &&
          Array.from({ length: 10 }).map((_, index) => <ListItemSkeleton key={`skeleton-${index}`} />)}

        {!isProductListLoading &&
          productList &&
          productList.productList.map((product) => (
            <ListItem
              key={product.productId}
              productId={product.productId}
              name={product.name}
              startDateTime={product.startDateTime}
              endDateTime={product.endDateTime}
              badge={product.badge}
              image={product.image}
              originalPrice={product.originalPrice}
              discountType={product.discountType}
              discountValue={product.discountValue}
              price={product.price}
              stock={product.optionList.reduce((sum, option) => sum + option.stock, 0)}
              isSoldOut={product.optionList.every((option) => option.stock === 0)}
            />
          ))}
      </ul>
    </section>
  );
}
